/* Parenting Made Easy — behaviour for the pme-* sections. */
(() => {
  /**
   * Publishes a custom event to Shopify Customer Events. The GA4 custom pixel in
   * Settings → Customer events subscribes to these names and forwards them.
   */
  function track(name, data = {}) {
    try {
      window.Shopify?.analytics?.publish?.(name, { page: location.pathname, ...data });
    } catch (e) {
      /* Tracking must never break the page. */
    }
  }

  /** Section key from the template, e.g. "hero", "problem", "product_hero". */
  function sectionName(el) {
    const wrapper = el?.closest('.shopify-section');
    return wrapper?.id?.split('__').pop() || 'unknown';
  }

  /** Adds lines through Shopify.actions so Horizon's cart drawer opens and refreshes. */
  async function addToCart(lines, discountCodes) {
    const updateCart = window.Shopify?.actions?.updateCart;
    const payload = { lines };
    if (discountCodes?.length) payload.discountCodes = discountCodes;
    const result = await updateCart(payload, { event: { context: 'product' } });
    if (result?.userErrors?.length) throw new Error(result.userErrors[0].message);
    if (!document.querySelector('theme-drawer#cart-drawer')) {
      window.location.href = (window.Theme?.routes?.cart_url) || '/cart';
    }
  }

  async function cartHasVariant(variantId) {
    try {
      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
      return cart.items.some((item) => String(item.variant_id) === String(variantId));
    } catch (e) {
      return false;
    }
  }

  /**
   * Shows the upsell popup for the main product and resolves with the shopper's choice:
   * true = add the offer too, false = main product only.
   */
  function askUpsell(dialog) {
    return new Promise((resolve) => {
      let settled = false;
      const finish = (accepted) => {
        if (settled) return;
        settled = true;
        dialog.removeEventListener('close', onClose);
        track(accepted ? 'pme_upsell_accept' : 'pme_upsell_decline');
        resolve(accepted);
      };
      const onClose = () => finish(false);
      dialog.addEventListener('close', onClose);
      dialog.querySelectorAll('[data-pme-upsell-decline]').forEach((btn) => {
        btn.onclick = () => {
          finish(false);
          dialog.close();
        };
      });
      dialog.querySelector('[data-pme-upsell-accept]').onclick = () => {
        finish(true);
        dialog.close();
      };
      dialog.onclick = (event) => {
        if (event.target === dialog) dialog.close();
      };
      dialog.showModal();
      track('pme_upsell_shown');
    });
  }

  function initBuyForms() {
    document.querySelectorAll('form[data-pme-buy]').forEach((form) => {
      if (form.dataset.pmeBound) return;
      form.dataset.pmeBound = 'true';

      form.addEventListener('submit', async (event) => {
        if (!window.Shopify?.actions?.updateCart) return; // Let the normal form post to /cart/add happen.

        event.preventDefault();
        const button = form.querySelector('button[type="submit"]');
        const error = form.querySelector('[data-pme-error]');
        const variantId = form.querySelector('input[name="id"]')?.value;
        if (error) error.textContent = '';
        if (button?.getAttribute('aria-busy') === 'true') return;
        button?.setAttribute('aria-busy', 'true');

        try {
          const lines = [{ merchandiseId: variantId, quantity: 1 }];
          let codes = [];
          const dialog = document.querySelector('dialog[data-pme-upsell]');
          const mainVariants = (dialog?.dataset.mainVariants || '').split(',');
          const offerVariant = dialog?.dataset.offerVariant;

          if (dialog && offerVariant && mainVariants.includes(String(variantId)) && !(await cartHasVariant(offerVariant))) {
            button?.removeAttribute('aria-busy');
            const accepted = await askUpsell(dialog);
            button?.setAttribute('aria-busy', 'true');
            if (accepted) {
              lines.push({ merchandiseId: offerVariant, quantity: 1 });
              if (dialog.dataset.discountCode) codes = [dialog.dataset.discountCode];
            }
          }

          await addToCart(lines, codes);
        } catch (err) {
          if (error) error.textContent = err?.message || 'Something went wrong. Please try again.';
        } finally {
          button?.removeAttribute('aria-busy');
        }
      });
    });
  }

  /** Scores the self-check and shows the matching result band. */
  function initQuizzes() {
    document.querySelectorAll('[data-pme-quiz]').forEach((quiz) => {
      const result = quiz.querySelector('[data-pme-quiz-result]');
      const score = quiz.querySelector('[data-pme-quiz-score]');
      const boxes = quiz.querySelectorAll('input[type="checkbox"]');
      const total = boxes.length;

      const update = () => {
        const checked = quiz.querySelectorAll('input[type="checkbox"]:checked').length;
        if (score) score.textContent = `${checked} of ${total}`;
        if (!result) return;
        if (checked === 0) {
          result.removeAttribute('data-active');
          return;
        }
        const ratio = checked / total;
        result.dataset.active = ratio >= 0.7 ? 'high' : ratio >= 0.4 ? 'mid' : 'low';
      };

      boxes.forEach((box) => box.addEventListener('change', update));
      update();
    });
  }

  /** Shows the sticky buy bar once the hero's call to action has scrolled away. */
  function initStickyBars() {
    document.querySelectorAll('[data-pme-sticky]').forEach((bar) => {
      const targets = document.querySelectorAll(bar.dataset.pmeSticky || '[data-pme-primary-cta]');
      if (!targets.length || !('IntersectionObserver' in window)) {
        bar.dataset.visible = 'true';
        return;
      }

      const visible = new Set();
      let passedFirst = false;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visible.add(entry.target);
          else visible.delete(entry.target);
          if (entry.target === targets[0] && !entry.isIntersecting && entry.boundingClientRect.top < 0) {
            passedFirst = true;
          }
          if (entry.target === targets[0] && entry.isIntersecting) passedFirst = false;
        });
        bar.dataset.visible = String(passedFirst && visible.size === 0);
      });
      targets.forEach((target) => observer.observe(target));
    });
  }

  /**
   * Records how far people get down the page (each section once, when it is properly in view),
   * which buttons they press, which FAQ questions they open and whether they use the self-check.
   */
  function initTracking() {
    if (window.__pmeTracking) return;
    window.__pmeTracking = true;

    const sections = [...document.querySelectorAll('main .shopify-section')].filter(
      (s) => s.querySelector('.pme') && !s.querySelector('.pme-sticky, dialog')
    );

    if (sections.length && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const enough = entry.intersectionRatio >= 0.3 || entry.intersectionRect.height >= window.innerHeight * 0.4;
            if (!enough) return;
            observer.unobserve(entry.target);
            track('pme_section_view', {
              section: sectionName(entry.target),
              position: sections.indexOf(entry.target) + 1,
              total: sections.length,
            });
          });
        },
        { threshold: [0, 0.1, 0.2, 0.3, 0.5] }
      );
      sections.forEach((section) => observer.observe(section));
    }

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target.closest('.pme-btn, .pme-upsell__decline');
        if (!target || target.closest('dialog[data-pme-upsell]')) return;
        const data = {
          section: target.closest('.pme-sticky') ? 'sticky_bar' : sectionName(target),
          label: (target.textContent || '').trim().slice(0, 60),
        };
        const quiz = target.closest('[data-pme-quiz]');
        if (quiz) data.quiz_score = quiz.querySelectorAll('input[type="checkbox"]:checked').length;
        track('pme_cta_click', data);
      },
      true
    );

    document.addEventListener(
      'toggle',
      (event) => {
        const details = event.target;
        if (!(details instanceof HTMLDetailsElement) || !details.open || !details.closest('.pme-faq')) return;
        track('pme_faq_open', {
          section: sectionName(details),
          question: (details.querySelector('summary')?.textContent || '').trim().slice(0, 100),
        });
      },
      true
    );

    document.addEventListener('change', (event) => {
      const quiz = event.target.closest?.('[data-pme-quiz]');
      if (!quiz || quiz.dataset.pmeTracked) return;
      quiz.dataset.pmeTracked = 'true';
      track('pme_quiz_start', { section: sectionName(quiz) });
    });
  }

  function init() {
    initBuyForms();
    initQuizzes();
    initStickyBars();
    initTracking();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
